<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t('pages.mountings.mounting.mountingRegistrations.modal.status.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col gap-y-5">
          <Multiselect
            v-model="rawData.status"
            :error="errors?.status"
            :options="possibleStatus"
            :label="$t('mountingTimeslotRegistration.status')"
            :can-deselect="false"
            :can-clear="false"
          />
          <text-area
            v-model="rawData.adminComment"
            :error="errors?.adminComment"
            :label="$t('mountingTimeslotRegistration.adminComment')"
            class="w-full"
            :disabled="!isAdmin"
          />
          <text-area
            v-model="rawData.comment"
            :error="errors?.comment"
            :label="$t('mountingTimeslotRegistration.comment')"
            class="w-full"
          />
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import { type Option } from '@utils/filter/filterTypes/Dropdown.vue';
import type {
  MountingTimeslotRegistration,
} from '@/entities/Entities';
import {useStore} from "@/store";

interface Props {
  open: boolean;
  mountingTimeslotRegistration?: MountingTimeslotRegistration;
}

const emit = defineEmits(['update:open', 'update:mountingTimeslotRegistration', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMountingTimeslotRegistration = useVModel(props, 'mountingTimeslotRegistration', emit);

const { t } = useI18n();

const notify = useNotify();

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const possibleStatus = ref<Option[]>();
enumOptionsProvider('/enums/MountingTimeslotRegistrationStatus', (item) => ({
  label: t(`mountingTimeslotRegistration.statusText.${item}`),
  value: item
}))().then((val) => {
  possibleStatus.value = val.filter((el) => el.value !== 'completed');
});

const initialData = (mountingTimeslotRegistration?: MountingTimeslotRegistration) => ({
  comment: undefined,
  adminComment: undefined,
  ...mountingTimeslotRegistration,
  status: (mountingTimeslotRegistration?.status === 'canceled'
    || mountingTimeslotRegistration?.status === 'missed'
    || mountingTimeslotRegistration?.status === 'other')
    ? mountingTimeslotRegistration?.status
    : undefined,
});

const rawData = syncRef(internalMountingTimeslotRegistration, (mountingTimeslotRegistration) => ({
  ...initialData(mountingTimeslotRegistration),
}));

const {
  data, errors, validatedFn, setValidation,
} = useValidation(
  object({
    adminComment: string().nullable(),
    comment: string().nullable(),
    status: string().nullable().required(t('validation.notEmpty')),
  }),
  rawData,
);

function resetData() {
  rawData.value = { ...initialData() };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec(props.mountingTimeslotRegistration?.['@id'] ?? '', {
      method: 'PUT',
      body: {
        ...(isAdmin ? { adminComment: data.value.adminComment } : {}),
        comment: data.value.comment,
        status: data.value.status,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.status.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.status.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
